import { useState, ReactNode, useCallback } from "react";
import styled, { css } from "styled-components";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import classNames from "classnames";

/** The `SectionContainer` component properties. */
export interface SectionContainerProps {
  readonly headerTitle: ReactNode;
  readonly abilityToClose?: boolean;
  readonly theme?: string;
  readonly readOnlyTheme?: boolean;
  readonly sectionTitleWithBtn?: boolean;
  readonly onHide?: Function;

  /** The component children. */
  readonly children?: ReactNode;
}

/** The section container root. */
const SectionContainerRoot = styled.div`
  box-shadow: 0 2px 7px 0 ${Color.toString(Color.withAlpha(Color.DarkGrey[800], 0.15))};
  border-radius: 0 0 3px 3px;
  margin: 30px 0;

  .section-container-content,
  .expandable-section-container-content {
    background-color: ${Color.toString(Color.LightGrey[50])};
    padding: 30px;
    opacity: 1;
    transition: opacity 0.3s ease;
    border-radius: 0 0 3px 3px;
  }

  &.sectionContainerWrapperClose {
    .section-container-header {
      background-color: ${Color.toString(Color.Green[400])};
    }

    .section-container-content,
    .expandable-section-container-content {
      height: 0;
      padding: 0;
      opacity: 0;
      overflow: hidden;
    }
  }

  .expandable-section-container-header {
    padding: 15px 5px;
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 20px;
    transition: all 0.1s ease;
    color: ${Color.toString(Color.DarkGrey[500])};

    .expandable-section-container-header-close-btn {
      top: 0;
      display: inline;
      cursor: pointer;

      &:hover {
        color: ${Color.toString(Color.DarkBlue[500])};

        &:after {
          color: ${Color.toString(Color.DarkBlue[500])};
        }
      }

      &:after {
        color: ${Color.toString(Color.LightGrey[500])};
        content: "\e906";
        font-family: "caprx-icon";
        margin-left: 10px;
        font-size: 0.56rem;
        display: inline;
        padding: 5px;
        border-radius: 50%;
        background-color: ${Color.toString(Color.LightGrey[200])};
        position: relative;
        top: -1px;
      }
    }
  }

  &.expandable-section-container-wrapper {
    box-shadow: none;
  }

  &.section-container-wrapper-close {
    .expandable-section-container-header {
      color: ${Color.toString(Color.DarkGrey[500])};

      .expandable-section-container-header-close-btn {
        &:hover {
          color: ${Color.toString(Color.DarkBlue[500])};
        }
      }
    }
  }

  .expandable-section-container-header-close {
    padding: 0px;
  }  

  .section-title {
    font-size: 18px;
    font-weight: 600;
  }
`;

/** The wrapper. */
const Wrapper = styled.div<{ $readOnly: boolean }>`
  ${({ $readOnly }) => $readOnly && css`
    .section-container-header {
      background-color: ${Color.toString(Color.Purple[500])};

      &:hover {
        background-color: ${Color.toString(Color.Purple[500])};
      }

      &.no-ability-to-close {
        cursor: default;

        &:hover {
          background-color: ${Color.toString(Color.Purple[500])};
        }
      }
    }    
  `}
`;

/** The container content. */
const ContainerContent = styled.div<{ $close: boolean }>`
  background-color: ${Color.toString(Color.LightGrey[50])};
  padding: 30px;
  opacity: 1;
  transition: opacity 0.3s ease;
  border-radius: 0 0 3px 3px;
  display: ${({ $close }) => $close ? "none" : "block"};
`;

/**
 * A section container.
 * @deprecated Please move away from this and build your own equivalent using updated components.
 * */
export const SectionContainer = (props: SectionContainerProps): ReactNode => {
  const { children, headerTitle, theme, abilityToClose = false, sectionTitleWithBtn, readOnlyTheme = false, onHide } = props;

  const [collapseSection, setCollapseSection] = useState(false);

  const closeSectionContainer = useCallback(() => {
    if (!abilityToClose) return;

    onHide?.(collapseSection);
    setCollapseSection(!collapseSection);
  }, [abilityToClose, collapseSection]);

  return (
    <SectionContainerRoot className={classNames([theme, "container-class"])}>
      <Wrapper $readOnly={readOnlyTheme}>
        <SectionHeader
          onClick={closeSectionContainer}
          collapseSection={collapseSection}
          headerTitle={headerTitle}
          abilityToClose={abilityToClose}
          sectionTitleWithBtn={sectionTitleWithBtn}
        />
      </Wrapper>
      <ContainerContent className="section-container-content" $close={collapseSection}>{children}</ContainerContent>
    </SectionContainerRoot>
  );
};

/** The section header root. */
const SectionHeaderRoot = styled.div<{ $allowClose: boolean, $hasButton: boolean }>`
  background-color: ${Color.toString(Color.Turqoise["500"])};
  color: ${(Color.toString(Color.White))};
  padding: 0 30px;
  min-height: 52px;
  font-weight: 600;
  font-size: 0.875rem;
  position: relative;
  transition: all 0.1s ease;
  text-transform: uppercase;
  border-radius: 3px 3px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ $hasButton }) => $hasButton && css`
    padding: 0 10px 0 30px;
  `}
  
  ${({ $allowClose }) => $allowClose && css`
    cursor: pointer;
    &:hover {
      background-color: ${Color.toString(Color.Turqoise["500"])};
    }
  `}

  ${({ $allowClose }) => !$allowClose && css`
    cursor: default;
    &:hover {
      background-color: ${Color.toString(Color.Turqoise["500"])};
    }
  `}

  div {
    + div {
      min-width: 20%;

      .react-loading-skeleton {
        background-color: ${Color.toString(Color.Turqoise["500"])};
        background-image: linear-gradient(90deg, ${Color.toString(Color.Green["400"])}, ${Color.toString(Color.Green["300"])}, ${Color.toString(Color.Green["400"])});
      }
    }
  }
`;

/** The close button. */
const CloseButton = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  display: inline-block;
  background-color: ${Color.toString(Color.withAlpha(Color.White, 0.1))};
  padding: 18px 25px;
  min-width: 110px;

  &:after {
    content: "\e906";
    font-family: "caprx-icon";
    margin-left: 10px;
    font-size: 12px;
    display: inline-block;
  }
`;

/** The arrow. */
const Arrow = styled.span`
  display: inline-block;
  position: absolute;
  width: 0px;
  right: 100%;
  height: 0px;
  border: 26px solid transparent;
  top: 0;
  border-right-color: ${Color.toString(Color.withAlpha(Color.White, 0.1))};
  border-left-width: 0px;
`;


/** The `SectionHeader` component properties. */
export interface SectionHeaderProps {
  readonly collapseSection: boolean;
  readonly headerTitle: ReactNode;
  readonly abilityToClose: boolean;
  readonly sectionTitleWithBtn?: boolean;
  readonly onClick: () => void;
}

/**
 * The section header component.
 * @deprecated Please move away from this and build your own equivalent using updated components.
 */
export const SectionHeader = (props: SectionHeaderProps): ReactNode => {
  const { collapseSection = false, onClick, headerTitle, abilityToClose = false, sectionTitleWithBtn = false } = props;

  return (
    <SectionHeaderRoot className="section-container-header" $allowClose={abilityToClose} $hasButton={sectionTitleWithBtn} onClick={onClick}>
      {headerTitle}
      {abilityToClose && (
        <CloseButton>
          <Arrow />
          {(collapseSection) ? "Show" : "Hide"}
        </CloseButton>
      )}
    </SectionHeaderRoot>
  );
};

